/**
 * @file X3b — turn the focus bus into the two labels a turn body may carry.
 * @module utils/xoe/pageContext
 *
 * The focus bus (`src/context/XoeFocusContext.tsx`) is a CLIENT object and carries more
 * than the wire does: `patientId` and the explicitly client-only `patientLabel` live on
 * the same `XoeFocus` that `routeLabel` and `sectionLabel` do. This module is the one
 * place that decides which of those fields become a request body.
 *
 * *** IT NAMES ITS FIELDS AND NEVER SPREADS. *** `{...focus}` would compile, pass a
 * "the route label is sent" test, and quietly widen every time someone adds a field to
 * `XoeFocus` — the next such field being the one nobody notices. Reading `routeLabel` and
 * `sectionLabel` by name makes "a new focus field cannot reach the wire" a property of
 * the code rather than of everybody's future care, and this module's test
 * "a field added to XoeFocus cannot reach the wire" dies if it stops being true.
 *
 * *** THE LABELS ARE ALREADY ALLOW-LISTED WHEN THEY ARRIVE HERE. *** `xoeRouteLabel()` /
 * `xoeSectionLabel()` resolve a pathname or a tab key THROUGH a table and return
 * `undefined` for anything they do not recognise, so a raw pathname, a query string or a
 * clinician-authored tab title cannot be in the values this module reads
 * (`XoeFocusContext.test.tsx` pins that end). There is a second, independent allow-list
 * server-side (`xoe-agent/app/agent/page_context.py`), which DROPS an unrecognised label
 * rather than echoing it. Neither is a reason to skip the other.
 */

import type { XoeFocus, XoeWheelFocus } from '../../types/xoe/XoeFocusTypes';
import type { XoeTurnContext, XoeWheelTurnContext, XoePresencePayload } from '../../types/xoe/XoeTypes';

/**
 * Named field by field, never spread — the same rule as the labels below, for the same
 * reason: a field added to `XoeWheelFocus` must not reach the wire until someone names it
 * here, after the agent knows it. `wheel_gene_id` is sent (as `null`) only while a gene is
 * selected; the real id arrives with the gene-ring work.
 */
const xoeWheelTurnContext = (wheel: XoeWheelFocus): XoeWheelTurnContext => {
	const out: XoeWheelTurnContext = { panel_id: wheel.panelId };
	if (wheel.traitId !== undefined) out.trait_id = wheel.traitId;
	if (wheel.wheelGeneId !== undefined) out.wheel_gene_id = wheel.wheelGeneId;
	return out;
};

/**
 * The turn context for the practitioner's current focus, or `undefined` when there is
 * nothing sendable.
 *
 * `undefined` — not `{}`, not a partial object — because `route_label` is REQUIRED
 * server-side: a context without one is a 422 that fails the whole turn, where sending
 * no context at all is exactly the pre-X3a behaviour the server still accepts. So an
 * unrecognised route (which `xoeRouteLabel()` resolves to `undefined`) costs the model
 * one note, never the answer.
 *
 * A `sectionLabel` WITHOUT a `routeLabel` is reachable, not hypothetical: the provider
 * spreads the patient focus over a `routeLabel` that is `undefined` on any screen the
 * route map does not know — and the map is hand-maintained, so a route added to
 * `App.tsx` without a label entry is exactly that screen, while a PatientModal opened on
 * it (from a page, or from a Xoe proposal's `bindAndOpen()`) still publishes its tab.
 * The orphan section is dropped here, for the same reason the server's own resolver
 * drops it: "Medications" alone does not locate the practitioner anywhere, and a note
 * that cannot say which screen invites the model to guess.
 */
export const xoeTurnContext = (focus: XoeFocus): XoeTurnContext | undefined => {
	if (!focus.routeLabel) return undefined;

	// Keys are OMITTED rather than set to `undefined`, so the object's own key set is
	// assertable — `JSON.stringify` would hide the difference, a `Object.keys` test does not.
	const context: XoeTurnContext = { route_label: focus.routeLabel };
	if (focus.sectionLabel) context.section_label = focus.sectionLabel;
	if (focus.wheel) context.wheel = xoeWheelTurnContext(focus.wheel);
	return context;
};

/** Named field by field, never spread — the same rule as `xoeTurnContext`, for the same reason. */
export const xoePresencePayload = (focus: XoeFocus, inConsult: boolean): XoePresencePayload => {
	const payload: XoePresencePayload = { in_consult: inConsult };
	if (focus.routeLabel) payload.route_label = focus.routeLabel;
	if (focus.sectionLabel) payload.section_label = focus.sectionLabel;
	return payload;
};
